import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

// ==================== TYPES ====================
// Mirrors GET /api/collection-summary?period=all's response shape.
interface PayModeBreakdown {
  amount: number;
  transaction_count: number;
}

// Collection split by whether the underlying fee record's own month_year
// matches the calendar month we're currently in ("current_month") or is
// for a different month — almost always arrears/backlog — paid during
// this period ("other_months"). Each side now carries its own pay-mode
// breakdown, so a separate pie can be drawn for each.
interface MonthRelationBreakdown {
  amount: number;
  transaction_count: number;
  by_pay_mode: Record<string, PayModeBreakdown>;
}

interface PeriodSummary {
  start_date: string;
  end_date: string;
  total_amount: number;
  transaction_count: number;
  by_pay_mode: Record<string, PayModeBreakdown>; // period total = sum of current_month + other_months
  current_month: MonthRelationBreakdown;
  other_months: MonthRelationBreakdown;
}

interface CollectionSummaryData {
  today: PeriodSummary;
  week: PeriodSummary;
  month: PeriodSummary;
}

interface CollectionSummaryResponse {
  status: string;
  message: string;
  data: CollectionSummaryData;
}

// "all" shows the three half-donut charts side by side (Today/Week/Month
// totals); the other three show a period's collection broken into three
// pies: This Month / Arrears / Total.
type PeriodKey = 'all' | 'today' | 'week' | 'month';
type SinglePeriodKey = 'today' | 'week' | 'month';

const PERIOD_LABELS: Record<PeriodKey, string> = {
  all: 'All',
  today: 'Today',
  week: 'Week',
  month: 'Month',
};

const PERIOD_SUBTITLES: Record<SinglePeriodKey, string> = {
  today: 'today',
  week: 'in the last 7 days',
  month: 'this month',
};

// Blue-family palette (stays inside the app's existing blue theme, no
// green/red semantic colors) — one shade per pay mode so the arcs stay
// visually distinct without leaving the theme.
const PAY_MODE_LABELS: Record<string, string> = {
  cash: 'Cash',
  online: 'Online',
  cheque: 'Cheque',
  unspecified: 'Other',
};

// Solid stroke colors for the half-donut arcs.
const PAY_MODE_SOLID_COLOR: Record<string, string> = {
  cash: '#3b82f6',
  online: '#06b6d4',
  cheque: '#6366f1',
  unspecified: '#94a3b8',
};

// Fixed sample split used to draw a placeholder half-donut when a period
// has no real data yet — just enough shape variation to read as a chart,
// not meant to represent anything.
const DUMMY_SPLIT: { key: string; pct: number }[] = [
  { key: 'cash', pct: 0.5 },
  { key: 'online', pct: 0.3 },
  { key: 'cheque', pct: 0.2 },
];

const formatCurrency = (amount: number): string =>
  `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Mixes a hex color toward white — used to derive the lighter rim tone
// drawn along the outer edge of each ring segment from its own solid color.
const lightenColor = (hex: string, amount: number): string => {
  const clean = hex.replace('#', '');
  const num = parseInt(clean, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

// ==================== HOOK ====================
// Fetches all three periods (today/week/month) in one request so switching
// the pill selector below is instant and needs no extra round trip.
const useCollectionSummary = (sessionId: string) => {
  const [data, setData] = useState<CollectionSummaryData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { period: 'all' };
      if (sessionId) params.session_id = sessionId;

      const response = await apiClient.get<CollectionSummaryResponse>('/api/collection-summary', { params });
      setData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch collection summary:', err);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, refetch: fetchData };
};

// ==================== ARC MATH ====================
// Half-donut = a 180° sweep from the left point (180°), through the top
// (270°), to the right point (360°) — standard SVG angle convention where
// 0° is right and angles increase clockwise.
const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number) => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const describeArc = (cx: number, cy: number, r: number, startAngle: number, endAngle: number): string => {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

// ==================== HALF-DONUT CHART ====================
// Generic pay-mode pie for ANY {amount, transactionCount, byPayMode}
// bundle — reused for the Today/Week/Month comparison in the "All" view,
// AND for the This-Month / Arrears / Total trio shown inside a single
// period's view.
//
// Visual style: thick rounded-cap gauge ring with a soft glow behind it
// (same look as a fitness-ring/gauge chart), plus a small floating badge
// that sits on top of the ring showing the dominant segment's share —
// the badge swaps to whichever segment is hovered. Colors are untouched
// (still the existing blue-family PAY_MODE_SOLID_COLOR palette). The
// amount/label readout stays BELOW the ring, exactly as before — it is
// never moved into the center.
interface DonutData {
  amount: number;
  transactionCount: number;
  byPayMode: Record<string, PayModeBreakdown>;
}

const HalfDonutChart: React.FC<{ label: string; data: DonutData | null; animated: boolean; uid: string; helpText?: string }> = ({
  label,
  data,
  animated,
  uid,
  helpText,
}) => {
  const size = 180;
  const r = 62;
  const strokeW = 24; // thicker ring to match the gauge-style reference
  const cx = size / 2;
  const cy = 90;
  const svgHeight = cy + strokeW / 2 + 10;
  const GAP_DEG = 3;

  const [hoveredMode, setHoveredMode] = useState<string | null>(null);

  const total = data?.amount ?? 0;
  const isPlaceholder = total <= 0;

  const rows = isPlaceholder
    ? DUMMY_SPLIT.map((d) => [d.key, { amount: d.pct, transaction_count: 0 }] as const)
    : Object.entries(data!.byPayMode)
        .filter(([, v]) => v.amount > 0)
        .sort((a, b) => b[1].amount - a[1].amount);

  const denominator = isPlaceholder ? 1 : total;

  let cumulative = 0;
  const segments = rows.map(([mode, v], i) => {
    const pct = v.amount / denominator;
    const rawStart = 180 + cumulative * 180;
    cumulative += pct;
    const rawEnd = 180 + cumulative * 180;
    const startAngle = rawStart + (i > 0 ? GAP_DEG / 2 : 0);
    const endAngle = rawEnd - (i < rows.length - 1 ? GAP_DEG / 2 : 0);
    return {
      mode,
      amount: v.amount,
      pct,
      startAngle,
      endAngle,
      color: isPlaceholder ? '#e2e8f0' : PAY_MODE_SOLID_COLOR[mode] ?? PAY_MODE_SOLID_COLOR.unspecified,
    };
  });

  const hoveredSegment = segments.find((s) => s.mode === hoveredMode);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 170px',
        minWidth: 170,
        opacity: animated ? 1 : 0,
        transform: animated ? 'scale(1)' : 'scale(0.88)',
        transition: 'opacity 0.5s ease, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {label}
        </span>
        {helpText && (
          <>
            <span
              className={`donut-help-${uid}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: '#dbeafe',
                border: '1.5px solid #60a5fa',
                cursor: 'help',
                flexShrink: 0,
              }}
            >
              <i className="pi pi-question" style={{ fontSize: 9, color: '#1e40af', fontWeight: 900 }}></i>
            </span>
            <Tooltip
              target={`.donut-help-${uid}`}
              content={helpText}
              position="top"
              style={{
                maxWidth: 220,
                fontSize: '0.7rem',
                backgroundColor: '#1e40af',
                color: 'white',
                borderRadius: 8,
                padding: '6px 10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
            />
          </>
        )}
      </div>
      <svg width={size} height={svgHeight} viewBox={`0 0 ${size} ${svgHeight}`} style={{ overflow: 'visible' }}>
        <defs>
          {/* Soft drop shadow under the solid ring segments */}
          <filter id={`donut-shadow-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1e40af" floodOpacity="0.2" />
          </filter>
          {/* Blurred glow rendered behind the ring, same colors as the segments */}
          <filter id={`donut-glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
        </defs>

        {/* dotted extension — a faint dashed continuation of the ring's
            circle below the visible half-donut, echoing the reference
            gauge's dotted track that completes the circle */}
        <path
          d={describeArc(cx, cy, r, 0, 180)}
          fill="none"
          stroke="#cbd5e1"
          strokeWidth={2}
          strokeLinecap="round"
          strokeDasharray="1 7"
          opacity={0.5}
        />

        {/* background track — soft tinted pill the ring sits inside, sized to
            match the new thicker stroke */}
        <path d={describeArc(cx, cy, r, 180, 360)} fill="none" stroke="#eef2ff" strokeWidth={strokeW + 4} strokeLinecap="round" />

        {/* glow layer: blurred, low-opacity duplicate of each segment sitting
            just behind the solid arc, giving the ring a soft luminous edge */}
        {!isPlaceholder &&
          segments.map((s) => (
            <path
              key={`glow-${s.mode}`}
              d={describeArc(cx, cy, r, s.startAngle, s.endAngle)}
              fill="none"
              stroke={s.color}
              strokeWidth={strokeW + 8}
              strokeLinecap="round"
              opacity={0.28}
              filter={`url(#donut-glow-${uid})`}
            />
          ))}

        {/* solid segments */}
        {segments.map((s) => {
          const isHovered = hoveredMode === s.mode;
          return (
            <path
              key={s.mode}
              d={describeArc(cx, cy, r, s.startAngle, s.endAngle)}
              fill="none"
              stroke={s.color}
              strokeWidth={isHovered ? strokeW + 4 : strokeW}
              strokeLinecap="round"
              opacity={isPlaceholder ? 0.65 : hoveredMode && !isHovered ? 0.4 : 1}
              filter={!isPlaceholder ? `url(#donut-shadow-${uid})` : undefined}
              onMouseEnter={() => !isPlaceholder && setHoveredMode(s.mode)}
              onMouseLeave={() => setHoveredMode(null)}
              style={{
                cursor: isPlaceholder ? 'default' : 'pointer',
                transition: 'stroke-width 0.15s ease, opacity 0.15s ease',
              }}
            />
          );
        })}

        {/* outer rim highlight — a thin lighter tint of each segment's own
            color traced just outside its solid arc, giving the ring the
            glassy light-blue-bordered edge from the reference gauge */}
        {!isPlaceholder &&
          segments.map((s) => (
            <path
              key={`rim-${s.mode}`}
              d={describeArc(cx, cy, r + strokeW / 2 + 1.5, s.startAngle, s.endAngle)}
              fill="none"
              stroke={lightenColor(s.color, 0.55)}
              strokeWidth={2.5}
              strokeLinecap="round"
              opacity={hoveredMode && hoveredMode !== s.mode ? 0.35 : 0.9}
              style={{ pointerEvents: 'none', transition: 'opacity 0.15s ease' }}
            />
          ))}
      </svg>
      {isPlaceholder ? (
        <>
          <div style={{ marginTop: -6, fontWeight: 800, fontSize: '1.1rem', color: '#cbd5e1' }}>--</div>
          <div style={{ fontSize: 10, color: '#cbd5e1', fontWeight: 600, marginTop: 2 }}>No data yet</div>
        </>
      ) : hoveredSegment ? (
        <>
          <div style={{ marginTop: -6, fontWeight: 800, fontSize: '1.1rem', color: hoveredSegment.color }}>
            {formatCurrency(hoveredSegment.amount)}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>
            {PAY_MODE_LABELS[hoveredSegment.mode] ?? hoveredSegment.mode} · {Math.round(hoveredSegment.pct * 100)}%
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              marginTop: -6,
              fontWeight: 800,
              fontSize: '1.1rem',
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {formatCurrency(total)}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginTop: 2 }}>
            {data!.transactionCount} payment{data!.transactionCount !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </div>
  );
};

// Shared color key explaining the arc colors once, instead of repeating a
// legend under every single chart.
const CollectionLegend: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginTop: 14, flexWrap: 'wrap' }}>
    {(['cash', 'online', 'cheque'] as const).map((mode) => (
      <div key={mode} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: PAY_MODE_SOLID_COLOR[mode] }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{PAY_MODE_LABELS[mode]}</span>
      </div>
    ))}
  </div>
);

// ==================== MONTH-RELATION BARS ====================
// Two SEPARATE progress bars — "This Month" and "Arrears" — each scaled
// against the period's total so their relative size is meaningful. Used
// under each Today/Week/Month donut in the "All" view (a per-pay-mode pie
// split isn't useful at that zoom level; the single-period view below
// handles that via its own three donuts instead).
const MonthRelationBars: React.FC<{
  currentMonth: MonthRelationBreakdown;
  otherMonths: MonthRelationBreakdown;
  totalAmount: number;
  compact?: boolean;
}> = ({ currentMonth, otherMonths, totalAmount, compact }) => {
  if (totalAmount <= 0) return null;

  const rows = [
    {
      key: 'current',
      label: 'This Month',
      amount: currentMonth.amount,
      textColor: '#1e40af',
      bar: 'linear-gradient(90deg, #bfdbfe 0%, #3b82f6 100%)',
    },
    {
      key: 'other',
      label: 'Arrears',
      amount: otherMonths.amount,
      textColor: '#0e7490',
      bar: 'linear-gradient(90deg, #a5f3fc 0%, #06b6d4 100%)',
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8, width: '100%', marginTop: compact ? 8 : 12 }}>
      {rows.map((r) => {
        const pct = (r.amount / totalAmount) * 100;
        return (
          <div key={r.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: compact ? 10 : 12, fontWeight: 700, color: '#64748b' }}>{r.label}</span>
              <span style={{ fontSize: compact ? 10 : 12, fontWeight: 800, color: r.textColor }}>{formatCurrency(r.amount)}</span>
            </div>
            <div style={{ background: '#eff6ff', borderRadius: 999, height: compact ? 5 : 7, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: r.bar }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Explains what each "All"-tab donut's total actually covers — hover text
// for the ? icon next to Today/Week/Month.
const ALL_TAB_HELP: Record<SinglePeriodKey, string> = {
  today: "Total fee collection received today, for any month's fees.",
  week: "Total fee collection received in the last 7 days, for any month's fees.",
  month: "Total fee collection received so far this calendar month, for any month's fees.",
};

// ==================== PERIOD PILL SWITCHER ====================
// Same "label + rounded count badge" shape as the All/Active/Done segmented
// control reference, re-themed to the app's blue palette: active pill gets
// a white background + blue text/badge, inactive pills stay muted gray.
// "All" has no count badge (its three periods overlap, so a summed count
// would be misleading) — it gets a small icon instead.
const PeriodSwitcher: React.FC<{
  data: CollectionSummaryData | null;
  selected: PeriodKey;
  onSelect: (key: PeriodKey) => void;
}> = ({ data, selected, onSelect }) => {
  const periods: PeriodKey[] = ['all', 'today', 'week', 'month'];

  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        background: '#f1f5f9',
        borderRadius: 999,
        padding: 3,
        border: '1px solid #e2e8f0',
      }}
    >
      {periods.map((key) => {
        const isActive = selected === key;
        const count = key !== 'all' && data ? data[key].transaction_count : null;
        return (
          <button
            key={key}
            onClick={() => onSelect(key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: key === 'all' ? '5px 10px' : '5px 8px 5px 10px',
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              background: isActive ? '#ffffff' : 'transparent',
              boxShadow: isActive ? '0 1px 4px rgba(30, 64, 175, 0.15)' : 'none',
              transition: 'background 0.2s ease, box-shadow 0.2s ease',
            }}
          >
            {key === 'all' && (
              <i
                className="pi pi-th-large"
                style={{ fontSize: 10, color: isActive ? '#1e40af' : '#94a3b8' }}
              ></i>
            )}
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: isActive ? '#1e40af' : '#94a3b8',
              }}
            >
              {PERIOD_LABELS[key]}
            </span>
            {count !== null && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 20,
                  padding: '1px 5px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  border: `1.5px solid ${isActive ? '#3b82f6' : '#cbd5e1'}`,
                  color: isActive ? '#1e40af' : '#94a3b8',
                  background: isActive ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ==================== MAIN CARD ====================
const TodayCollectionCard: React.FC = () => {
  const activeSessionId = useSchoolDetailsStore(
    (state) => state.schoolDetails?.current_session?.id ?? ''
  );

  const { data, loading } = useCollectionSummary(activeSessionId);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>('all');
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, [data, selectedPeriod]);

  const hasAnyData = !!data && (data.today.total_amount > 0 || data.week.total_amount > 0 || data.month.total_amount > 0);

  const activeSummary: PeriodSummary | null = data && selectedPeriod !== 'all' ? data[selectedPeriod] : null;
  const periodHasData = !!activeSummary && activeSummary.total_amount > 0;

  return (
    <Card
      style={{
        borderRadius: '16px',
        marginBottom: '24px',
        border: '1px solid rgba(59, 130, 246, 0.15)',
        background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Header — title on the left, All/Today/Week/Month pill switcher on the right */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 6px 16px rgba(59, 130, 246, 0.25)',
            }}
          >
            <i className="pi pi-wallet" style={{ color: 'white', fontSize: '0.9rem' }}></i>
          </div>
          <h6 style={{ color: '#1e40af', fontWeight: 700, margin: 0, fontSize: '0.95rem' }}>
            Fees Collection Summary
          </h6>
        </div>

        <PeriodSwitcher data={data} selected={selectedPeriod} onSelect={setSelectedPeriod} />
      </div>

      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: '36px 0',
          }}
        >
          <style>{`
            @keyframes todayCollectionSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i
              className="pi pi-sync"
              style={{ color: 'white', fontSize: '1.1rem', animation: 'todayCollectionSpin 1s linear infinite' }}
            ></i>
          </div>
          <div style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading collection summary...</div>
        </div>
      ) : selectedPeriod === 'all' ? (
        <>
          {/* Today / Week / Month totals compared side by side. Each donut
              is paired with its own This-Month-vs-Arrears split, as two
              separate progress bars underneath it. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 16,
              paddingTop: 4,
            }}
          >
            {(['today', 'week', 'month'] as const).map((key) => {
              const summary = data ? data[key] : null;
              return (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 1 180px', minWidth: 180 }}>
                  <HalfDonutChart
                    label={PERIOD_LABELS[key]}
                    data={summary ? { amount: summary.total_amount, transactionCount: summary.transaction_count, byPayMode: summary.by_pay_mode } : null}
                    animated={animated}
                    uid={key}
                    helpText={ALL_TAB_HELP[key]}
                  />
                  {summary && summary.total_amount > 0 && (
                    <MonthRelationBars
                      currentMonth={summary.current_month}
                      otherMonths={summary.other_months}
                      totalAmount={summary.total_amount}
                      compact
                    />
                  )}
                </div>
              );
            })}
          </div>
          {hasAnyData && <CollectionLegend />}
          {!hasAnyData && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontSize: 11,
                color: '#cbd5e1',
                fontStyle: 'italic',
                marginTop: 10,
              }}
            >
              <i className="pi pi-info-circle" style={{ fontSize: 11 }}></i>
              No payments recorded yet — sample layout shown above.
            </div>
          )}
        </>
      ) : (
        <>
          {/* This Month / Arrears / Total — three pies for the selected
              period, each hoverable to inspect its own pay-mode split.
              "Total" is literally This-Month + Arrears summed, so no
              extra request is needed for it. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 16,
              paddingTop: 4,
            }}
          >
            <HalfDonutChart
              label="This Month"
              data={
                activeSummary
                  ? {
                      amount: activeSummary.current_month.amount,
                      transactionCount: activeSummary.current_month.transaction_count,
                      byPayMode: activeSummary.current_month.by_pay_mode,
                    }
                  : null
              }
              animated={animated}
              uid={`${selectedPeriod}-current`}
              helpText={`Payments received ${PERIOD_SUBTITLES[selectedPeriod as SinglePeriodKey]} that are for the CURRENT month's fees.`}
            />
            <HalfDonutChart
              label="Arrears"
              data={
                activeSummary
                  ? {
                      amount: activeSummary.other_months.amount,
                      transactionCount: activeSummary.other_months.transaction_count,
                      byPayMode: activeSummary.other_months.by_pay_mode,
                    }
                  : null
              }
              animated={animated}
              uid={`${selectedPeriod}-other`}
              helpText={`Payments received ${PERIOD_SUBTITLES[selectedPeriod as SinglePeriodKey]} that are for a DIFFERENT (older) month's fees — backlog caught up on.`}
            />
            <HalfDonutChart
              label="Total"
              data={
                activeSummary
                  ? {
                      amount: activeSummary.total_amount,
                      transactionCount: activeSummary.transaction_count,
                      byPayMode: activeSummary.by_pay_mode,
                    }
                  : null
              }
              animated={animated}
              uid={`${selectedPeriod}-total`}
              helpText={`This Month + Arrears combined — everything collected ${PERIOD_SUBTITLES[selectedPeriod as SinglePeriodKey]}.`}
            />
          </div>
          {periodHasData && <CollectionLegend />}
          {!periodHasData && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontSize: 11,
                color: '#cbd5e1',
                fontStyle: 'italic',
                marginTop: 10,
              }}
            >
              <i className="pi pi-info-circle" style={{ fontSize: 11 }}></i>
              No collections recorded {PERIOD_SUBTITLES[selectedPeriod as SinglePeriodKey]} — sample layout shown above.
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default TodayCollectionCard;